import React from 'react';
import { useTranslation } from 'react-i18next';
import { Btn, Label, Num, Rule } from '../Common/OkoylUI';

const ATTRIBUTE_IDS = ['strength', 'dexterity', 'intelligence', 'vitality'];
const MAX_VALUE = 30;

const AttributesAllocation = ({ character, onAttributeChange, onNext, onBack }) => {
    const { t } = useTranslation();

    const change = (attribute, delta) => {
        const current = character.attributes[attribute];
        const next = current + delta;
        if (delta > 0 && (character.points <= 0 || next > MAX_VALUE)) return;
        if (delta < 0 && next < 1) return;
        onAttributeChange({
            ...character,
            attributes: { ...character.attributes, [attribute]: next },
            points: character.points - delta,
        });
    };

    const pluralPoint = character.points > 1
        ? t('characterCreation.step2.remainingPointsPlural')
        : t('characterCreation.step2.remainingPointsSingular');

    return (
        <div>
            <h2 className="okcard-title">{t('characterCreation.step2.title')}</h2>
            <Rule thin />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Label color="gold">{t('characterCreation.step2.remainingPoints')}</Label>
                <Num size="lg" color="var(--okoyl-200)">{character.points}</Num>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                {ATTRIBUTE_IDS.map(id => {
                    const value = character.attributes[id];
                    const pct = Math.min(100, (value / MAX_VALUE) * 100);
                    return (
                        <div
                            key={id}
                            style={{
                                background: 'var(--ash-900)',
                                border: '1px solid rgba(200,144,42,0.18)',
                                borderRadius: 'var(--r-md)',
                                padding: 14,
                            }}
                        >
                            <Label>{t(`heroStats.labels.${id}`)}</Label>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '10px 0' }}>
                                <Btn variant="secondary" small onClick={() => change(id, -1)} disabled={value <= 1}>−</Btn>
                                <Num size="lg" color="var(--okoyl-200)">{value}</Num>
                                <Btn variant="secondary" small onClick={() => change(id, 1)} disabled={character.points === 0 || value >= MAX_VALUE}>+</Btn>
                            </div>
                            <div className="okbar-track" style={{ height: 6 }}>
                                <div className="okbar-fill okbar-xp" style={{ width: pct + '%' }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            <Rule />

            <Label color="gold">{t('characterCreation.step2.preview')}</Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--ash-200)' }}>
                <div>{t('characterCreation.step2.previewLines.maxHealth')}</div>
                <div style={{ textAlign: 'right' }}><Num size="md" color="var(--okoyl-200)">{character.attributes.vitality * 10 + 50}</Num> PV</div>
                <div>{t('characterCreation.step2.previewLines.physicalDamage')}</div>
                <div style={{ textAlign: 'right' }}><Num size="md" color="var(--okoyl-200)">{character.attributes.strength * 2 + 5}</Num></div>
                <div>{t('characterCreation.step2.previewLines.magicalDamage')}</div>
                <div style={{ textAlign: 'right' }}><Num size="md" color="var(--okoyl-200)">{character.attributes.intelligence * 3}</Num></div>
                <div>{t('characterCreation.step2.previewLines.dodge')}</div>
                <div style={{ textAlign: 'right' }}><Num size="md" color="var(--okoyl-200)">{Math.round(character.attributes.dexterity * 0.5 * 10) / 10}%</Num></div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28 }}>
                <Btn variant="secondary" onClick={onBack}>◀ {t('common.back')}</Btn>
                <Btn variant="primary" onClick={onNext} disabled={character.points > 0}>
                    {character.points > 0
                        ? t('characterCreation.step2.remainingPointsButton', { count: character.points, plural: pluralPoint })
                        : `${t('common.next')} ▶`}
                </Btn>
            </div>
        </div>
    );
};

export default AttributesAllocation;
